import { useQuery } from '@tanstack/react-query'
import { type PaginationState, type SortingState } from '@tanstack/react-table'

import { type Reimbursement } from 'components/admin/types'
import { fetchWithHeaders } from 'utils'

import { GrapeAPIError, type GrapeAPIErrorType } from '../GrapeAPIError'
import { type PaginatedResponse } from '../types'

export type ReimbursementSingleValueFilters = {
  has_receipts?: string
  after_date?: string
  before_date?: string
  vip_employer?: string
  off_thatch_or_medicare?: string
}

export type ReimbursementArrayValueFilters = {
  states?: string[]
  purposes?: string[]
  member_ids?: string[]
  employer_ids?: string[]
  broker_ids?: string[]
  enrollment_categories?: string[]
}

export type ReimbursementsFilters = ReimbursementSingleValueFilters &
  ReimbursementArrayValueFilters

type GetReimbursementsQueryArgs = {
  filters: ReimbursementsFilters
  pagination: PaginationState
  sorting: SortingState
}

export function getReimbursementsQueryKey ({
  filters,
  pagination,
  sorting,
}: GetReimbursementsQueryArgs) {
  return [
    'admin',
    'reimbursements',
    'filters',
    filters,
    'pagination',
    pagination,
    'sorting',
    sorting,
  ]
}

export function useGetReimbursements ({
  filters,
  pagination,
  sorting,
}: GetReimbursementsQueryArgs) {
  return useQuery<PaginatedResponse<Reimbursement>, GrapeAPIErrorType>({
    queryKey: getReimbursementsQueryKey({ filters, pagination, sorting }),
    queryFn: async () => {
      const searchParams = new URLSearchParams()

      searchParams.set('page', pagination.pageIndex.toString())
      searchParams.set('items', pagination.pageSize.toString())

      if (sorting.length > 0) {
        const { id, desc } = sorting[0]
        searchParams.set(`sort[${id}]`, desc ? 'desc' : 'asc')
      }

      Object.entries(filters).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          val.forEach((v) => searchParams.append(`filter[${key}][]`, v))
        } else if (val) {
          searchParams.append(`filter[${key}]`, val.toString())
        }
      })

      const response = await fetchWithHeaders(
        `/api/web/admin/reimbursements?${searchParams.toString()}`
      )
      if (!response.ok) {
        const errorJson = await response.json()
        throw GrapeAPIError({
          status: response.status,
          message: errorJson.message,
          errors: errorJson.errors,
        })
      }

      return response.json()
    },
  })
}
